import { useMemo, useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
// Direct imports to avoid circular dependency through the components barrel
import { SuggestionReviewModal } from '../SuggestionReviewModal';
import { AskTillyModal } from '../concierge/AskTillyModal';
import { colors, fonts, fontWeights, radius, spacing } from '@/theme/tokens';
import { useBusinessStore } from '@/stores';
import { useAssistantFeed } from './useAssistantFeed';
import { AssistantFeedCard } from './AssistantFeedCard';
import { AssistantFeedSkeleton } from './AssistantFeedSkeleton';

interface AssistantFeedProps {
  maxVisible?: number;
  collapsible?: boolean;
}

export function AssistantFeed({ maxVisible = 3, collapsible = false }: AssistantFeedProps) {
  const { t: rawT } = useTranslation(['admin']);
  const {
    items,
    isLoading,
    isEmpty,
    refetch,
    isRefetching,
    isBusy,
    activeSuggestion,
    setActiveSuggestion,
  } = useAssistantFeed();
  const [expanded, setExpanded] = useState(!collapsible);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [askTillyOpen, setAskTillyOpen] = useState(false);
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);

  const t = (key: string, options?: Record<string, unknown>) =>
    rawT(key as never, options as never) as unknown as string;

  // Directly answers "ok I signed up, now what?" — these are deterministic
  // suggestions (no LLM call until the owner picks one or types their own),
  // per RFD-7's guardrail that the product should stay bounded and predictable.
  const nowWhatQuickReplies = [
    t('admin:assistantFeed.askTillyModal.quickReplies.todaysAttention'),
    t('admin:assistantFeed.askTillyModal.quickReplies.next'),
    t('admin:assistantFeed.askTillyModal.quickReplies.addServices'),
    t('admin:assistantFeed.askTillyModal.quickReplies.setUpReminders'),
  ];

  const filteredItems = useMemo(
    () => items.filter((item) => !dismissedIds.includes(item.id)),
    [dismissedIds, items]
  );

  const collapsedCount = Math.max(filteredItems.length - maxVisible, 0);
  const visibleItems =
    collapsible && !expanded ? filteredItems.slice(0, maxVisible) : filteredItems;

  const dismissLocally = (itemId: string) => {
    setDismissedIds((current) => (current.includes(itemId) ? current : [...current, itemId]));
  };

  return (
    <>
      <SuggestionReviewModal
        open={!!activeSuggestion}
        suggestion={activeSuggestion}
        onClose={() => setActiveSuggestion(null)}
        onReviewComplete={(suggestionId) => {
          dismissLocally(`suggestion-${suggestionId}`);
        }}
      />
      {activeBusinessId && (
        <AskTillyModal
          open={askTillyOpen}
          onClose={() => setAskTillyOpen(false)}
          businessId={activeBusinessId}
          title={t('admin:assistantFeed.askTillyModal.title')}
          description={t('admin:assistantFeed.askTillyModal.description')}
          placeholder={t('admin:assistantFeed.askTillyModal.placeholder')}
          quickReplies={nowWhatQuickReplies}
        />
      )}
      {/* Spin keyframe for the refresh button */}
      <style>{`
        @keyframes tilly-feed-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* ── Header ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: spacing[4],
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
            <div>
              <div
                style={{
                  fontFamily: fonts.body,
                  fontSize: '12px',
                  fontWeight: fontWeights.semibold,
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '2px',
                }}
              >
                {t('admin:assistantFeed.title')}
              </div>
              <div
                style={{
                  fontFamily: fonts.body,
                  fontSize: '14px',
                  fontWeight: fontWeights.medium,
                  color: colors.textPrimary,
                }}
              >
                {t('admin:assistantFeed.subtitle')}
              </div>
            </div>
            {filteredItems.length > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 20,
                  height: 20,
                  borderRadius: radius.full,
                  backgroundColor: colors.brass,
                  color: colors.white,
                  fontSize: '11px',
                  fontWeight: fontWeights.bold,
                  padding: '0 5px',
                  lineHeight: 1,
                }}
              >
                {filteredItems.length}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing[1] }}>
            {/* Ask Tilly — the "ok I signed up, now what?" entry point */}
            <button
              onClick={() => setAskTillyOpen(true)}
              disabled={!activeBusinessId}
              aria-label={t('admin:assistantFeed.askTillyAria')}
              style={{
                background: 'none',
                border: 'none',
                cursor: activeBusinessId ? 'pointer' : 'default',
                opacity: activeBusinessId ? 1 : 0.4,
                color: colors.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: spacing[1],
                borderRadius: radius.md,
                transition: 'color 150ms ease',
              }}
            >
              <Sparkles size={16} />
            </button>

            {/* Manual refresh button */}
            <button
              onClick={() => refetch()}
              aria-label={t('admin:assistantFeed.refresh')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: spacing[1],
                borderRadius: radius.md,
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = colors.accent;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
              }}
            >
              <RefreshCw
                size={16}
                style={{
                  animation: isRefetching ? 'tilly-feed-spin 1s linear infinite' : undefined,
                }}
              />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        {isLoading ? (
          <AssistantFeedSkeleton />
        ) : isEmpty || filteredItems.length === 0 ? (
          <div
            style={{
              padding: `${spacing[8]} 0`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: spacing[2],
            }}
          >
            <div
              style={{
                fontFamily: fonts.body,
                fontWeight: fontWeights.semibold,
                fontSize: '15px',
                color: colors.textSecondary,
              }}
            >
              {t('admin:assistantFeed.empty.title')}
            </div>
            <div
              style={{
                fontFamily: fonts.body,
                fontSize: '13px',
                color: colors.textMuted,
              }}
            >
              {t('admin:assistantFeed.empty.description')}
            </div>
            {activeBusinessId && (
              <button
                type="button"
                onClick={() => setAskTillyOpen(true)}
                style={{
                  marginTop: spacing[2],
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing[1],
                  background: 'none',
                  border: `1px solid ${colors.accent}`,
                  borderRadius: radius.md,
                  color: colors.accent,
                  cursor: 'pointer',
                  fontFamily: fonts.body,
                  fontSize: '13px',
                  fontWeight: fontWeights.semibold,
                  padding: `${spacing[2]} ${spacing[3]}`,
                }}
              >
                <Sparkles size={14} />
                {t('admin:assistantFeed.empty.askTilly')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[3],
              }}
            >
              {visibleItems.map((item) => (
                <AssistantFeedCard
                  key={item.id}
                  item={item}
                  isBusy={isBusy}
                  onPrimaryAction={item.ctaAction}
                  onDismiss={
                    item.dismissAction
                      ? async () => {
                          try {
                            await item.dismissAction?.();
                            dismissLocally(item.id);
                          } catch {
                            // The mutation owns user-facing error handling.
                          }
                        }
                      : undefined
                  }
                  onSnooze={
                    item.snoozeAction
                      ? async () => {
                          try {
                            await item.snoozeAction?.();
                            dismissLocally(item.id);
                          } catch {
                            // The mutation owns user-facing error handling.
                          }
                        }
                      : undefined
                  }
                />
              ))}
            </div>

            {collapsedCount > 0 && !expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.accent,
                  fontSize: '14px',
                  fontWeight: fontWeights.semibold,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[1],
                  padding: `${spacing[3]} 0 0`,
                  fontFamily: fonts.body,
                }}
              >
                {t('admin:assistantFeed.expandMore', { count: collapsedCount })}
              </button>
            )}

            {collapsible && expanded && filteredItems.length > maxVisible && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[1],
                  padding: `${spacing[3]} 0 0`,
                  fontFamily: fonts.body,
                }}
              >
                {t('admin:assistantFeed.showLess')}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}
